import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import {
    Scale,
    FileText,
    KeyRound,
    Building2,
    Stamp,
    HeartHandshake,
    MapPin,
    Phone,
    Mail,
    Clock,
    Menu,
    X,
    ChevronRight,
    ShieldCheck,
    Sparkles,
    BookOpen,
} from "lucide-react";
import { Toaster } from "./components/ui/sonner";
import ContactModal from "./components/ContactModal";
import ComingSoon from "./components/ComingSoon";

// 🔁 Activar/desactivar el modo "Próximamente"
// Para REACTIVAR el sitio completo, cambia esto a false (y redespliega).
const SHOW_COMING_SOON = false;

const LOGO_URL =
    "https://customer-assets.emergentagent.com/job_notaria-guadalupe/artifacts/qubtu0de_N25_sinfondo%202.webp";
const HERO_BG =
    "https://static.prod-images.emergentagent.com/jobs/f985e4be-924d-4995-bfd4-1be0ba48e8e7/images/601f5b4adcb868aa0cd8c3e6b437e2fde1bbbbc16c269b2ec392ad8fdf46b546.png";
const PROCESS_BG =
    "https://static.prod-images.emergentagent.com/jobs/f985e4be-924d-4995-bfd4-1be0ba48e8e7/images/56f7e03fe7f63e9307e5704b58ed6d1fe97e4cbcce0aac27be7c1b7149b46018.png";
const OFFICE_IMG =
    "https://images.unsplash.com/photo-1715593949273-09009558300a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxlbGVnYW50JTIwb2ZmaWNlJTIwaW50ZXJpb3J8ZW58MHx8fHwxNzc4ODU4OTA4fDA&ixlib=rb-4.1.0&q=85";

const NAV_LINKS = [
    { label: "Servicios", href: "#servicios" },
    { label: "Nosotros", href: "#nosotros" },
    { label: "Proceso", href: "#proceso" },
    { label: "Ubicación", href: "#ubicacion" },
];

const SERVICES = [
    {
        icon: Building2,
        title: "Compraventa de inmuebles",
        body:
            "Escrituración de propiedades, asesoría fiscal y revisión integral de antecedentes registrales para una transmisión segura.",
    },
    {
        icon: BookOpen,
        title: "Testamentos",
        body:
            "Otorgue su voluntad con plena certeza jurídica. Asesoría personalizada y resguardo confidencial de su disposición.",
    },
    {
        icon: KeyRound,
        title: "Poderes notariales",
        body:
            "Poderes generales y especiales para actos jurídicos, administración y pleitos, con redacción precisa y conforme a ley.",
    },
    {
        icon: Scale,
        title: "Constitución de sociedades",
        body:
            "Sociedades mercantiles, civiles y reformas estatutarias. Acompañamos su proyecto desde su nacimiento legal.",
    },
    {
        icon: Stamp,
        title: "Actas y fe de hechos",
        body:
            "Constancia notarial de hechos, asambleas, notificaciones e interpelaciones con valor probatorio pleno.",
    },
    {
        icon: HeartHandshake,
        title: "Donaciones",
        body:
            "Donaciones entre familiares o terceros, con planeación patrimonial y asesoría sobre implicaciones fiscales.",
    },
];

const PROCESS_STEPS = [
    {
        n: "01",
        title: "Consulta inicial",
        body:
            "Escuchamos su caso, valoramos los documentos y le orientamos sobre el instrumento notarial idóneo.",
    },
    {
        n: "02",
        title: "Análisis y preparación",
        body:
            "Revisión registral, fiscal y documental. Elaboramos el proyecto del instrumento con precisión técnica.",
    },
    {
        n: "03",
        title: "Firma y otorgamiento",
        body:
            "Acompañamos el acto notarial con la solemnidad y confidencialidad que su asunto exige.",
    },
    {
        n: "04",
        title: "Inscripción y entrega",
        body:
            "Tramitamos su inscripción ante las autoridades competentes y le entregamos su testimonio.",
    },
];

const TRUST_POINTS = [
    {
        icon: ShieldCheck,
        title: "Seguridad jurídica",
        body: "Cada acto goza de fe pública y certeza plena.",
    },
    {
        icon: Sparkles,
        title: "Atención de excelencia",
        body: "Asesoría personalizada y trato discreto.",
    },
    {
        icon: Clock,
        title: "Eficiencia",
        body: "Procesos claros, tiempos definidos.",
    },
];

function Navbar({ onOpenContact }) {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="site-navbar"
            className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
                scrolled ? "glass-nav py-3" : "bg-transparent py-5"
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between">
                <a href="#inicio" data-testid="nav-logo" className="flex items-center gap-3">
                    <img
                        src={LOGO_URL}
                        alt="Notaría 25"
                        className="h-12 w-12 object-contain"
                    />
                    <div className="hidden sm:block leading-tight">
                        <div className="font-serif-display text-lg text-[#F3F4F6]">
                            Notaría 25
                        </div>
                        <div className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]/80">
                            Guadalupe · Chihuahua
                        </div>
                    </div>
                </a>

                <nav className="hidden md:flex items-center gap-10">
                    {NAV_LINKS.map((l) => (
                        <a
                            key={l.href}
                            href={l.href}
                            data-testid={`nav-link-${l.label.toLowerCase()}`}
                            className="nav-link"
                        >
                            {l.label}
                        </a>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <button
                        onClick={onOpenContact}
                        data-testid="nav-contact-cta"
                        className="hidden md:inline-flex btn-outline-gold !py-2.5 !px-5 !text-[0.72rem]"
                    >
                        Contacto
                    </button>
                    <button
                        onClick={() => setOpen((v) => !v)}
                        data-testid="nav-mobile-toggle"
                        className="md:hidden w-10 h-10 flex items-center justify-center border border-[#D4AF37]/30 text-[#D4AF37]"
                        aria-label="Menú"
                    >
                        {open ? <X size={16} /> : <Menu size={16} />}
                    </button>
                </div>
            </div>

            {open && (
                <div className="md:hidden glass-nav border-t border-[#D4AF37]/10 mt-3">
                    <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-5">
                        {NAV_LINKS.map((l) => (
                            <a
                                key={l.href}
                                href={l.href}
                                onClick={() => setOpen(false)}
                                className="nav-link"
                                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                            >
                                {l.label}
                            </a>
                        ))}
                        <button
                            onClick={() => {
                                setOpen(false);
                                onOpenContact();
                            }}
                            className="btn-outline-gold w-full"
                            data-testid="mobile-nav-contact-cta"
                        >
                            Contacto
                        </button>
                    </div>
                </div>
            )}
        </header>
    );
}

function Hero({ onOpenContact }) {
    return (
        <section
            id="inicio"
            data-testid="hero-section"
            className="relative min-h-screen flex items-center overflow-hidden"
        >
            <div
                className="absolute inset-0 bg-cover bg-center opacity-40"
                style={{ backgroundImage: `url(${HERO_BG})` }}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-[#08111D]/85 via-[#08111D]/65 to-[#08111D]" />
            <div className="absolute inset-0 hero-grain" />

            <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-32 pb-20 grid lg:grid-cols-12 gap-12 items-center w-full">
                <div className="lg:col-span-8 animate-fade-up">
                    {/* Mobile emblem — visible only on small screens */}
                    <div className="lg:hidden flex justify-center mb-10 animate-fade-in">
                        <div className="relative">
                            <div className="absolute -inset-3 border border-[#D4AF37]/20" />
                            <div className="absolute -inset-6 border border-[#D4AF37]/10" />
                            <img
                                src={LOGO_URL}
                                alt="Notaría 25"
                                className="relative w-44 h-44 sm:w-56 sm:h-56 object-contain"
                                data-testid="hero-logo-mobile"
                            />
                        </div>
                    </div>

                    <p className="overline animate-fade-in delay-100">
                        Notaría 25 · Guadalupe, Chihuahua
                    </p>
                    <h1
                        data-testid="hero-title"
                        className="font-serif-display text-5xl md:text-7xl lg:text-[5.5rem] font-light text-[#F3F4F6] mt-6 leading-[0.95] tracking-tight"
                    >
                        Certeza jurídica,
                        <br />
                        <span className="italic text-[#D4AF37]">tradición notarial.</span>
                    </h1>
                    <p className="text-[#9CA3AF] text-lg md:text-xl mt-8 max-w-xl leading-relaxed font-light">
                        En Notaría N25 combinamos la solidez jurídica con una visión
                        moderna y eficiente del servicio notarial. Nuestro compromiso es
                        brindar atención personalizada, confianza y certeza legal en cada
                        trámite, ofreciendo soluciones ágiles y transparentes para
                        personas, familias y empresas.
                    </p>

                    <div className="flex flex-col sm:flex-row gap-4 mt-12">
                        <button
                            onClick={onOpenContact}
                            data-testid="hero-contact-cta"
                            className="btn-gold"
                        >
                            Agendar consulta
                            <ChevronRight size={16} />
                        </button>
                        <a
                            href="#servicios"
                            data-testid="hero-services-cta"
                            className="btn-outline-gold"
                        >
                            Ver servicios
                        </a>
                    </div>

                    <div className="mt-16 flex flex-wrap gap-x-10 gap-y-5 text-sm">
                        {TRUST_POINTS.map((t, i) => (
                            <div
                                key={t.title}
                                className={`flex items-center gap-3 animate-fade-up delay-${(i + 2) * 100}`}
                            >
                                <t.icon size={16} className="text-[#D4AF37]" />
                                <span className="text-[#F3F4F6]/85">{t.title}</span>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="hidden lg:flex lg:col-span-4 justify-end">
                    <div className="relative">
                        <div className="absolute -inset-4 border border-[#D4AF37]/20" />
                        <div className="absolute -inset-8 border border-[#D4AF37]/10" />
                        <img
                            src={LOGO_URL}
                            alt="Notaría 25 emblema"
                            className="relative w-72 h-72 object-contain"
                        />
                    </div>
                </div>
            </div>

            <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-[#9CA3AF]/60">
                <span className="text-[10px] tracking-[0.3em] uppercase">Desliza</span>
                <div className="w-px h-10 bg-gradient-to-b from-[#D4AF37]/60 to-transparent" />
            </div>
        </section>
    );
}

function Services({ onOpenContact }) {
    return (
        <section
            id="servicios"
            data-testid="services-section"
            className="relative py-24 md:py-32 bg-[#08111D]"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10">
                <div className="grid lg:grid-cols-12 gap-12 mb-16">
                    <div className="lg:col-span-5">
                        <p className="overline">Nuestros servicios</p>
                        <h2 className="font-serif-display text-4xl md:text-5xl font-light text-[#F3F4F6] mt-4 leading-tight">
                            Cada acto notarial,
                            <br />
                            <span className="italic text-[#D4AF37]">una obra de precisión.</span>
                        </h2>
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7 flex items-end">
                        <p className="text-[#9CA3AF] text-base md:text-lg leading-relaxed">
                            Brindamos servicios notariales integrales con la
                            confidencialidad y solemnidad que cada documento amerita. Conozca
                            las áreas en las que podemos asesorarle.
                        </p>
                    </div>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {SERVICES.map((s, i) => (
                        <article
                            key={s.title}
                            data-testid={`service-card-${i}`}
                            className="service-card group"
                        >
                            <div className="flex items-start justify-between mb-8">
                                <div className="w-12 h-12 flex items-center justify-center border border-[#D4AF37]/30 text-[#D4AF37] group-hover:border-[#D4AF37]/70 transition-colors">
                                    <s.icon size={22} strokeWidth={1.4} />
                                </div>
                                <span className="font-serif-display text-[#D4AF37]/30 text-2xl italic">
                                    0{i + 1}
                                </span>
                            </div>
                            <h3 className="font-serif-display text-2xl font-normal text-[#F3F4F6] leading-snug">
                                {s.title}
                            </h3>
                            <p className="text-[#9CA3AF] mt-4 text-[0.95rem] leading-relaxed">
                                {s.body}
                            </p>
                            <button
                                onClick={onOpenContact}
                                data-testid={`service-cta-${i}`}
                                className="mt-6 inline-flex items-center gap-2 text-[#D4AF37] text-xs uppercase tracking-[0.18em] hover:gap-3 transition-all"
                            >
                                Consultar <ChevronRight size={14} />
                            </button>
                        </article>
                    ))}
                </div>
            </div>
        </section>
    );
}

function About() {
    return (
        <section
            id="nosotros"
            data-testid="about-section"
            className="relative py-24 md:py-32 bg-[#0A1628]"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 items-center">
                <div className="lg:col-span-6 order-2 lg:order-1">
                    <div className="relative">
                        <div className="absolute -inset-3 border border-[#D4AF37]/25" />
                        <img
                            src={OFFICE_IMG}
                            alt="Despacho Notaría 25"
                            className="relative w-full h-[480px] object-cover grayscale-[10%] contrast-105"
                        />
                        <div className="absolute -bottom-6 -right-6 bg-[#08111D] border border-[#D4AF37]/30 px-8 py-6 hidden md:block">
                            <div className="font-serif-display text-4xl text-[#D4AF37]">25</div>
                            <div className="text-[10px] tracking-[0.3em] uppercase text-[#F3F4F6]/70 mt-1">
                                Notaría · Chihuahua
                            </div>
                        </div>
                    </div>
                </div>

                <div className="lg:col-span-5 lg:col-start-8 order-1 lg:order-2">
                    <p className="overline">Quiénes somos</p>
                    <h2 className="font-serif-display text-4xl md:text-5xl font-light text-[#F3F4F6] mt-4 leading-tight">
                        Una tradición de
                        <br />
                        <span className="italic text-[#D4AF37]">confianza notarial.</span>
                    </h2>
                    <p className="text-[#9CA3AF] mt-8 text-base md:text-lg leading-relaxed">
                        En Notaría 25 entendemos que cada firma representa decisiones
                        trascendentes para usted y los suyos.
                    </p>
                    <p className="text-[#9CA3AF] mt-4 text-base md:text-lg leading-relaxed">
                        Atendemos personalmente cada caso en nuestras oficinas en Guadalupe,
                        Chihuahua, con un equipo profesional comprometido con la legalidad,
                        la confidencialidad y la excelencia.
                    </p>

                    <div className="grid grid-cols-3 gap-6 mt-12 pt-10 border-t border-[#D4AF37]/15">
                        {[
                            { v: "+25", l: "Años de trayectoria" },
                            { v: "+5K", l: "Actos otorgados" },
                            { v: "100%", l: "Confidencialidad" },
                        ].map((s) => (
                            <div key={s.l}>
                                <div className="font-serif-display text-3xl md:text-4xl text-[#D4AF37]">
                                    {s.v}
                                </div>
                                <div className="text-[10px] tracking-[0.22em] uppercase text-[#9CA3AF] mt-2">
                                    {s.l}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

function Process() {
    return (
        <section
            id="proceso"
            data-testid="process-section"
            className="relative py-24 md:py-32 overflow-hidden"
        >
            <div
                className="absolute inset-0 bg-cover bg-center opacity-15"
                style={{ backgroundImage: `url(${PROCESS_BG})` }}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-[#08111D] via-[#08111D]/95 to-[#08111D]" />

            <div className="relative max-w-7xl mx-auto px-6 md:px-10">
                <div className="text-center max-w-2xl mx-auto">
                    <p className="overline">Nuestro proceso</p>
                    <h2 className="font-serif-display text-4xl md:text-5xl font-light text-[#F3F4F6] mt-4 leading-tight">
                        Pasos claros,
                        <span className="italic text-[#D4AF37]"> resultado inquebrantable.</span>
                    </h2>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#D4AF37]/15 mt-16 border border-[#D4AF37]/15">
                    {PROCESS_STEPS.map((step) => (
                        <div
                            key={step.n}
                            data-testid={`process-step-${step.n}`}
                            className="bg-[#08111D] p-8 md:p-10 group hover:bg-[#0A1628] transition-colors"
                        >
                            <div className="flex items-center gap-3">
                                <span className="font-serif-display text-5xl text-[#D4AF37] leading-none">
                                    {step.n}
                                </span>
                                <div className="flex-1 h-px bg-[#D4AF37]/30" />
                            </div>
                            <h3 className="font-serif-display text-2xl font-normal text-[#F3F4F6] mt-6 leading-snug">
                                {step.title}
                            </h3>
                            <p className="text-[#9CA3AF] mt-3 text-sm leading-relaxed">
                                {step.body}
                            </p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function Location({ onOpenContact }) {
    return (
        <section
            id="ubicacion"
            data-testid="location-section"
            className="relative py-24 md:py-32 bg-[#0A1628]"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 items-center">
                <div className="lg:col-span-5">
                    <p className="overline">Visite nuestra notaría</p>
                    <h2 className="font-serif-display text-4xl md:text-5xl font-light text-[#F3F4F6] mt-4 leading-tight">
                        Guadalupe,
                        <br />
                        <span className="italic text-[#D4AF37]">Chihuahua, México.</span>
                    </h2>
                    <p className="text-[#9CA3AF] mt-6 text-base md:text-lg leading-relaxed">
                        Le recibimos en un espacio diseñado para la asesoría discreta y
                        profesional. Agende una cita y conozca personalmente nuestro
                        servicio.
                    </p>

                    <div className="mt-10 space-y-5">
                        <div className="flex items-start gap-4">
                            <div className="w-10 h-10 flex items-center justify-center border border-[#D4AF37]/30 text-[#D4AF37] shrink-0">
                                <MapPin size={16} />
                            </div>
                            <div>
                                <p className="text-[10px] tracking-[0.22em] uppercase text-[#9CA3AF] mb-1">
                                    Dirección
                                </p>
                                <p className="text-[#F3F4F6] text-base">
                                    Calle Miguel Hidalgo 237 A
                                    <br />
                                    Guadalupe, Chihuahua, México
                                </p>
                            </div>
                        </div>

                        <div className="flex items-start gap-4">
                            <div className="w-10 h-10 flex items-center justify-center border border-[#D4AF37]/30 text-[#D4AF37] shrink-0">
                                <Mail size={16} />
                            </div>
                            <div>
                                <p className="text-[10px] tracking-[0.22em] uppercase text-[#9CA3AF] mb-1">
                                    Correo
                                </p>
                                <a
                                    href="mailto:amgmayoral@notaria25.mx"
                                    className="text-[#F3F4F6] text-base hover:text-[#D4AF37] transition-colors"
                                    data-testid="location-email"
                                >
                                    amgmayoral@notaria25.mx
                                </a>
                            </div>
                        </div>

                        <div className="flex items-start gap-4">
                            <div className="w-10 h-10 flex items-center justify-center border border-[#D4AF37]/30 text-[#D4AF37] shrink-0">
                                <Clock size={16} />
                            </div>
                            <div>
                                <p className="text-[10px] tracking-[0.22em] uppercase text-[#9CA3AF] mb-1">
                                    Horario
                                </p>
                                <p className="text-[#F3F4F6] text-base">
                                    Lun – Vie · 9:00 a 18:00
                                </p>
                            </div>
                        </div>
                    </div>

                    <button
                        onClick={onOpenContact}
                        data-testid="location-contact-cta"
                        className="btn-gold mt-10"
                    >
                        Agendar cita
                        <ChevronRight size={16} />
                    </button>
                </div>

                <div className="lg:col-span-7">
                    <div className="relative border border-[#D4AF37]/25">
                        <iframe
                            title="Ubicación Notaría 25"
                            src="https://www.google.com/maps?q=Calle+Miguel+Hidalgo+237+A,+Guadalupe,+Chihuahua,+Mexico&output=embed"
                            className="w-full h-[460px] grayscale contrast-110"
                            style={{
                                filter: "grayscale(80%) invert(92%) hue-rotate(180deg) brightness(0.92)",
                            }}
                            loading="lazy"
                            data-testid="location-map"
                        />
                    </div>
                </div>
            </div>
        </section>
    );
}

function Footer({ onOpenContact }) {
    return (
        <footer
            data-testid="site-footer"
            className="relative bg-[#08111D] border-t border-[#D4AF37]/15 pt-20 pb-10"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10">
                <div className="grid md:grid-cols-12 gap-10 pb-16 border-b border-[#D4AF37]/10">
                    <div className="md:col-span-5">
                        <div className="flex items-center gap-3">
                            <img src={LOGO_URL} alt="Notaría 25" className="h-14 w-14 object-contain" />
                            <div>
                                <div className="font-serif-display text-xl text-[#F3F4F6]">
                                    Notaría 25
                                </div>
                                <div className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]/80">
                                    Guadalupe · Chihuahua
                                </div>
                            </div>
                        </div>
                        <p className="text-[#9CA3AF] mt-6 max-w-sm leading-relaxed text-sm">
                            Asesoría notarial elegante, moderna y de absoluta confianza para
                            quienes valoran la solemnidad de lo bien hecho.
                        </p>
                    </div>

                    <div className="md:col-span-3">
                        <p className="overline">Navegación</p>
                        <ul className="mt-5 space-y-3">
                            {NAV_LINKS.map((l) => (
                                <li key={l.href}>
                                    <a
                                        href={l.href}
                                        className="text-[#F3F4F6]/80 hover:text-[#D4AF37] text-sm transition-colors"
                                        data-testid={`footer-link-${l.label.toLowerCase()}`}
                                    >
                                        {l.label}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="md:col-span-4">
                        <p className="overline">Contacto</p>
                        <ul className="mt-5 space-y-3 text-sm">
                            <li className="flex items-center gap-3 text-[#F3F4F6]/85">
                                <Mail size={14} className="text-[#D4AF37]" />
                                amgmayoral@notaria25.mx
                            </li>
                            <li className="flex items-start gap-3 text-[#F3F4F6]/85">
                                <MapPin size={14} className="text-[#D4AF37] mt-0.5 shrink-0" />
                                <span>Calle Miguel Hidalgo 237 A, Guadalupe, Chihuahua, México</span>
                            </li>
                            <li className="flex items-center gap-3 text-[#F3F4F6]/85">
                                <Clock size={14} className="text-[#D4AF37]" />
                                Lun – Vie · 9:00 a 18:00
                            </li>
                        </ul>
                        <button
                            onClick={onOpenContact}
                            data-testid="footer-contact-cta"
                            className="btn-outline-gold mt-7 !py-2.5 !px-5 !text-[0.72rem]"
                        >
                            Escribirnos
                        </button>
                    </div>
                </div>

                <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-8">
                    <p className="text-[10px] tracking-[0.22em] uppercase text-[#9CA3AF]">
                        © {new Date().getFullYear()} Notaría 25 · Todos los derechos reservados
                    </p>
                    <p className="text-[10px] tracking-[0.22em] uppercase text-[#9CA3AF]/70">
                        Fe pública · Discreción · Excelencia
                    </p>
                </div>
            </div>
        </footer>
    );
}

const Home = () => {
    const [contactOpen, setContactOpen] = useState(false);
    const openContact = () => setContactOpen(true);

    return (
        <div data-testid="home-page" className="bg-[#08111D] text-[#F3F4F6] min-h-screen">
            <Navbar onOpenContact={openContact} />
            <Hero onOpenContact={openContact} />
            <Services onOpenContact={openContact} />
            <About />
            <Process />
            <Location onOpenContact={openContact} />
            <Footer onOpenContact={openContact} />
            <ContactModal open={contactOpen} onOpenChange={setContactOpen} />
        </div>
    );
};

function App() {
    return (
        <div className="App">
            <Toaster
                theme="dark"
                position="top-right"
                toastOptions={{
                    style: {
                        background: "#0A1628",
                        border: "1px solid rgba(212,175,55,0.25)",
                        color: "#F3F4F6",
                    },
                }}
            />
            <BrowserRouter>
                <Routes>
                    <Route
                        path="/"
                        element={SHOW_COMING_SOON ? <ComingSoon /> : <Home />}
                    />
                    {/* Vista previa del sitio completo siempre disponible en /sitio */}
                    <Route path="/sitio" element={<Home />} />
                </Routes>
            </BrowserRouter>
        </div>
    );
}

export default App;
